/* src/router/index.js */
import { createRouter, createWebHistory } from 'vue-router'

// Lazy-loaded views
const HomeView = () => import('@/views/HomeView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')
const CountriesView = () => import('@/views/CountriesView.vue')
const LeaderboardView = () => import('@/views/LeaderboardView.vue')
const PlaceBetSuccessView = () => import('@/views/PlaceBetSuccessView.vue')

// Lobbies
const VirtualLobbyView = () => import('@/views/VirtualLobbyView.vue')
const CrashGamesView = () => import('@/views/CrashGamesView.vue')
const InstantGamesView = () => import('@/views/InstantGamesView.vue')
const CasinoGamesView = () => import('@/views/CasinoGamesView.vue')
const PragmaticGamesView = () => import('@/views/PragmaticGamesView.vue')
const ShackGamesView = () => import('@/views/ShackGamesView.vue')

// Virtual Games
const LeagueView = () => import('@/views/LeagueView.vue')
const TurboView = () => import('@/views/TurboView.vue')
const JackpotView = () => import('@/views/JackpotView.vue')

// Crash Games
const AviatorView = () => import('@/views/AviatorView.vue')
const AviatrixView = () => import('@/views/AviatrixView.vue')
const JetXView = () => import('@/views/JetXView.vue')

// Jackpot (separate files)
const JackpotResultsView = () => import('@/views/JackpotResultsView.vue')
const JackpotHowToPlayView = () => import('@/views/JackpotHowToPlay.vue')

// Account
const MyAccountView = () => import('@/views/account/MyAccountView.vue')
const MyBetsView = () => import('@/views/account/MyBetsView.vue')
const BetResultsView = () => import('@/views/account/BetResultsView.vue')
const DepositView = () => import('@/views/account/DepositView.vue')
const WithdrawView = () => import('@/views/account/WithdrawView.vue')
const PromotionsView = () => import('@/views/account/PromotionsView.vue')
const PromotionDetailsView = () => import('@/views/account/PromotionDetailsView.vue')
const FaqsView = () => import('@/views/FaqsView.vue')

// Auth
const LoginView = () => import('@/views/auth/LoginView.vue')
const RegisterView = () => import('@/views/auth/RegisterView.vue')
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView.vue')
const VerifyAccountView = () => import('@/views/auth/VerifyAccountView.vue')
const ResetPasswordView = () => import('@/views/auth/ResetPasswordView.vue')
const AccountCreatedView = () => import('@/views/auth/AccountCreatedView.vue')

// More markets (prematch and live)
const MoreMarketsView = () => import('@/views/MoreMarketsView.vue')
const MoreMarketsLiveView = () => import('@/views/MoreMarketsLiveView.vue')

// Search
const SearchPageView = () => import('@/views/SearchPageView.vue')

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Home
    { path: '/', name: 'home', component: HomeView, meta: { layout: 'home', showBetSlip: true } },
    {
      path: '/leaderboard',
      name: 'leaderboard',
      component: LeaderboardView,
      meta: { layout: 'default', showBetSlip: true },
    },
    {
      path: '/freebet',
      name: 'freebet',
      component: () => import('@/views/FreebetView.vue'),
      meta: { layout: 'default', showBetSlip: true },
    },
    {
      path: '/live-games',
      name: 'live',
      component: () => import('@/views/LiveGamesView.vue'),
      meta: { layout: 'default', showBetSlip: true },
    },

    {
      path: '/more-markets/:match_id',
      name: 'MoreMarkets',
      component: MoreMarketsView,
      props: true,
      meta: { layout: 'default', showBetSlip: true },
    },

    // Live More Markets Route
    {
      path: '/live-markets/:match_id',
      name: 'MoreMarketsLive',
      component: MoreMarketsLiveView,
      props: true,
      meta: { layout: 'default', showBetSlip: true },
    },

    // Search (betslip visible ✅)
    {
      path: '/search',
      name: 'search',
      component: SearchPageView,
      meta: { layout: 'default', showBetSlip: true },
    },

    // Place Bet Success
    {
      path: '/place-bet/success',
      name: 'PlaceBetSuccess',
      component: PlaceBetSuccessView,
      meta: { layout: 'default', showBetSlip: true, requiresAuth: true },
    },

    // Markets / Lobbies (betslip hidden)
    {
      path: '/virtual-lobby',
      name: 'virtual-lobby',
      component: VirtualLobbyView,
      meta: { layout: 'lobby', sidebar: 'fast', showBetSlip: false },
    },
    {
      path: '/crash-games',
      name: 'crash-games',
      component: CrashGamesView,
      meta: { layout: 'lobby', sidebar: 'fast', showBetSlip: false },
    },
    {
      path: '/instant-games',
      name: 'instant-games',
      component: InstantGamesView,
      meta: { layout: 'lobby', sidebar: 'fast', showBetSlip: false },
    },
    {
      path: '/casino-games',
      name: 'casino-games',
      component: CasinoGamesView,
      meta: { layout: 'lobby', sidebar: 'fast', showBetSlip: false },
    },
    {
      path: '/pragmatic-games',
      name: 'PragmaticGames',
      component: PragmaticGamesView,
      meta: { layout: 'lobby', sidebar: 'fast', showBetSlip: false },
    },
    {
      path: '/shack-games',
      name: 'ShackGames',
      component: ShackGamesView,
      meta: { layout: 'lobby', sidebar: 'fast', showBetSlip: false },
    },

    // Virtual Games (default layout with sidebar for virtual games)
    {
      path: '/league',
      name: 'league',
      component: LeagueView,
      meta: { layout: 'game', showBetSlip: false },
    },
    {
      path: '/turbo',
      name: 'turbo',
      component: TurboView,
      meta: { layout: 'game', showBetSlip: false },
    },

    {
      path: '/box',
      name: 'box',
      component: () => import('@/views/BoxView.vue'),
      meta: { layout: 'game', showBetSlip: false },
    },

    // Crash Games (game layout - fullscreen experience)
    {
      path: '/aviator',
      name: 'aviator',
      component: AviatorView,
      meta: { layout: 'game', showBetSlip: false },
    },
    {
      path: '/aviatrix',
      name: 'aviatrix',
      component: AviatrixView,
      meta: { layout: 'game', showBetSlip: false },
    },
    {
      path: '/jetx',
      name: 'jetx',
      component: JetXView,
      meta: { layout: 'game', showBetSlip: false },
    },

    // Aliases
    { path: '/instant-promos', name: 'instant-promos', redirect: { name: 'instant-games' } },
    {
      path: '/instant-game-promos',
      name: 'instant-game-promos',
      redirect: { name: 'instant-games' },
    },
    {
      path: '/instant-games-lobby',
      name: 'instant-games-lobby',
      redirect: { name: 'instant-games' },
    },
    {
      path: '/play/:providerId/:gameId',
      name: 'InstantGamePlayer',
      component: () => import('@/views/InstantGamePlayerView.vue'),
      props: true,
      meta: { layout: 'iframe', showBetSlip: false },
    },
    { path: '/game-overview', redirect: { name: 'aviator' } },

    // Countries
    {
      path: '/countries',
      name: 'countries',
      component: CountriesView,
      meta: { layout: 'default', showBetSlip: true },
    },

    // Jackpot (default layout with betslip)
    {
      path: '/jackpot',
      name: 'jackpot',
      component: JackpotView,
      meta: { layout: 'default', showBetSlip: true },
    },
    {
      path: '/jackpot-results',
      name: 'jackpot-results',
      component: JackpotResultsView,
      meta: { layout: 'default', showBetSlip: true },
    },
    {
      path: '/jackpot-how-to-play',
      name: 'jackpot-how-to-play',
      component: JackpotHowToPlayView,
      meta: { layout: 'default', showBetSlip: true },
    },

    // Account
    {
      path: '/my-account',
      name: 'my-account',
      component: MyAccountView,
      meta: {
        layout: 'default',
        showBetSlip: true,
        requiresAuth: true,
        hideAccessAccount: true,
        showDeposit: true,
      },
    },
    {
      path: '/deposit',
      name: 'deposit',
      component: DepositView,
      meta: { layout: 'default', showBetSlip: true, requiresAuth: true },
    },
    {
      path: '/withdraw',
      name: 'withdraw',
      component: WithdrawView,
      meta: { layout: 'default', showBetSlip: true, requiresAuth: true },
    },
    {
      path: '/faqs',
      name: 'faqs',
      component: FaqsView,
      meta: { layout: 'lobby', showBetSlip: false, hideAccessAccount: true },
    },
    {
      path: '/terms-and-conditions',
      name: 'terms-and-conditions',
      component: () => import('@/views/TermsAndConditionsView.vue'),
      meta: { layout: 'lobby', showBetSlip: false, hideAccessAccount: true },
    },

    {
      path: '/how-to-play',
      name: 'how-to-play',
      component: () => import('@/views/HowToPlayView.vue'),
      meta: { layout: 'lobby', showBetSlip: false, hideAccessAccount: true },
    },

    {
      path: '/responsible-gambling',
      name: 'responsible-gambling',
      component: () => import('@/views/ResponsibleGamblingView.vue'),
      meta: { layout: 'lobby', showBetSlip: false, hideAccessAccount: true },
    },

    // My Bets — must have SIDEBAR + BETSLIP per design
    {
      path: '/my-bets',
      name: 'my-bets',
      component: MyBetsView,
      meta: { layout: 'default', showBetSlip: true, requiresAuth: true },
    },
    {
      path: '/bet-results/:id',
      name: 'bet-results',
      component: BetResultsView,
      meta: { layout: 'default', showBetSlip: true, requiresAuth: true },
    },

    // Additional protected routes
    {
      path: '/transactions',
      name: 'transactions',
      component: () => import('@/views/account/TransactionsView.vue'),
      meta: { layout: 'default', showBetSlip: true, requiresAuth: true },
    },
    {
      path: '/self-exclude-from-betting',
      name: 'self-exclude',
      component: () => import('@/views/account/SelfExcludeView.vue'),
      meta: { layout: 'home', showBetSlip: true, requiresAuth: true },
    },

    // Auth
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: {
        layout: 'auth',
        guest: true,
        showBetSlip: false,
        hideAccessAccount: true,
        hideSidebar: true,
      },
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterView,
      meta: {
        layout: 'auth',
        guest: true,
        showBetSlip: false,
        hideAccessAccount: true,
        hideSidebar: true,
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPasswordView,
      meta: {
        layout: 'auth',
        guest: true,
        showBetSlip: false,
        hideAccessAccount: true,
        hideSidebar: true,
      },
    },
    {
      path: '/verify',
      name: 'verify',
      component: VerifyAccountView,
      meta: {
        layout: 'auth',
        guest: true,
        showBetSlip: false,
        hideAccessAccount: true,
        hideSidebar: true,
      },
    },
    {
      path: '/verify/:acc_no',
      name: 'verify-account',
      component: VerifyAccountView,
      meta: {
        layout: 'auth',
        guest: true,
        showBetSlip: false,
        hideAccessAccount: true,
        hideSidebar: true,
      },
      props: true,
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: ResetPasswordView,
      meta: {
        layout: 'auth',
        guest: true,
        showBetSlip: false,
        hideAccessAccount: true,
        hideSidebar: true,
      },
    },
    {
      path: '/account-created',
      name: 'account-created',
      component: AccountCreatedView,
      meta: {
        layout: 'auth',
        requiresAuth: true,
        requiresAccountCreatedAccess: true,
        showBetSlip: false,
        hideAccessAccount: true,
        hideSidebar: true,
      },
    },

    // Promotions
    {
      path: '/promotions',
      name: 'promotion',
      component: PromotionsView,
      meta: { layout: 'lobby', showBetSlip: true, requiresAuth: false },
    },
    {
      path: '/promotion/:id',
      name: 'PromotionDetails',
      component: PromotionDetailsView,
      props: true,
      meta: { layout: 'default', showBetSlip: true, requiresAuth: false },
    },

    // 404
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundView,
      meta: { layout: 'home', showBetSlip: false },
    },
  ],
  scrollBehavior() {
    return { top: 0, left: 0 }
  },
})

// Route guards
router.beforeEach(async (to, from, next) => {
  // Import auth store dynamically to avoid circular dependency
  const { useAuthStore } = await import('@/stores/auth')
  const authStore = useAuthStore()

  // Hide mount element for iframe layout routes
  const mountEl = document.querySelector('[data-vue-app]')
  if (mountEl) {
    if (to.meta?.layout === 'iframe') {
      mountEl.style.display = 'none'
    } else {
      mountEl.style.display = ''
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login' })
    return
  }

  // Check if route requires account created access
  if (to.meta.requiresAccountCreatedAccess && !authStore.canAccessAccountCreated) {
    next({ name: 'home' })
    return
  }

  // Check if authenticated user is trying to access guest-only pages
  if (to.meta.guest && authStore.isAuthenticated) {
    next({ name: 'home' })
    return
  }

  next()
})

export default router
